'use client';

/**
 * SyncBusProvider
 *
 * Mounts one SSE connection per opportunity and exposes the sync bus
 * to all descendants via React context.
 *
 * Usage:
 *   <SyncBusProvider opportunityId={id} role="client">
 *     <App /> // descendants use useOnSyncEvent() and useSyncBusContext()
 *   </SyncBusProvider>
 */

import { type ReactNode } from 'react';
import { useSyncBus, SyncBusContext } from '@/hooks/use-sync-bus';
import type { SyncRole } from '@/lib/types/sync-events';

interface SyncBusProviderProps {
  children: ReactNode;
  opportunityId: string | undefined;
  role: SyncRole;
  enabled?: boolean;
}

export function SyncBusProvider({
  children,
  opportunityId,
  role,
  enabled = true,
}: SyncBusProviderProps) {
  const bus = useSyncBus(enabled ? opportunityId : undefined, role);

  return (
    <SyncBusContext.Provider value={bus}>
      {children}
    </SyncBusContext.Provider>
  );
}
